import { useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Flag, Globe, List, Loader2, Network, RefreshCw } from 'lucide-react'
import RoomNotFound from '@/components/PageNotFound/RoomNotFound'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import ROUTES from '@/routes/routes'
import { useWorldGraph } from '../hooks/useWorldGraph'
import { NEGATIVE, POSITIVE, ownerColorsFrom } from '../graphColors'
import type { StockType, WorldGraphNode, WorldGraphState } from '../types'
import GraphCanvas from '../components/GraphCanvas'
import RoundPanel from '../components/RoundPanel'

type View = 'rounds' | 'teams' | 'graph'

const TYPE_ORDER: StockType[] = ['Lever', 'Accumulator', 'Level', 'Drifter']

interface Group {
  key: string
  title: string
  /** The room team a roster team plays as, when its name differs. */
  playsAs?: string
  missingTeam?: boolean
  nodes: WorldGraphNode[]
}

/** Stocks grouped by the team that holds them, roster order first, then shared. */
function groupsOf(state: WorldGraphState): Group[] {
  const byOwner = new Map<string, WorldGraphNode[]>()
  const roster = new Set(state.teams.map((t) => t.id))
  for (const node of state.graph.nodes) {
    const key = node.owner && roster.has(node.owner) ? node.owner : ''
    byOwner.set(key, [...(byOwner.get(key) ?? []), node])
  }
  const sorted = (nodes: WorldGraphNode[]) =>
    [...nodes].sort(
      (a, b) =>
        TYPE_ORDER.indexOf(a.type) - TYPE_ORDER.indexOf(b.type) ||
        a.label.localeCompare(b.label)
    )
  const groups: Group[] = state.team_setup.teams
    .filter((entry) => byOwner.has(entry.id))
    .map((entry) => ({
      key: entry.id,
      title: entry.name,
      playsAs:
        entry.team && entry.team.name !== entry.name
          ? entry.team.name
          : undefined,
      missingTeam: !entry.team,
      nodes: sorted(byOwner.get(entry.id) ?? []),
    }))
  if (byOwner.has('')) {
    groups.push({
      key: '',
      title: 'Shared and uncontrolled',
      nodes: sorted(byOwner.get('') ?? []),
    })
  }
  return groups
}

function Change({ node }: { node: WorldGraphNode }) {
  const start = node.baseline_value ?? node.value
  const delta = Math.round((node.value - start) * 10) / 10
  if (!delta) return <span className='text-muted-foreground'>—</span>
  // For an inverted stock a rise is bad news.
  const good = node.inverted ? delta < 0 : delta > 0
  return (
    <span style={{ color: good ? POSITIVE : NEGATIVE }}>
      {delta > 0 ? '+' : ''}
      {delta}
    </span>
  )
}

/** The label without its team's name in front, under that team's heading. */
function shortLabel(label: string, team: string): string {
  const prefix = `${team}: `
  return team && label.startsWith(prefix) ? label.slice(prefix.length) : label
}

function StockRow({ node, team }: { node: WorldGraphNode; team: string }) {
  const value = Math.max(0, Math.min(100, node.value))
  return (
    <tr className='border-t align-middle'>
      <td className='px-3 py-2'>
        <div className='font-medium text-dark-blue'>
          {shortLabel(node.label, team)}
        </div>
        {node.inverted && (
          <div className='text-xs text-muted-foreground'>Low is good</div>
        )}
      </td>
      <td className='px-3 py-2'>
        <Badge variant='secondary'>{node.type}</Badge>
      </td>
      <td className='w-1/3 px-3 py-2'>
        <div
          className='h-2 rounded-full bg-gray-100'
          role='meter'
          aria-label={`${node.label} value`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={value}
        >
          <div
            className='h-2 rounded-full bg-dark-blue'
            style={{ width: `${value}%` }}
          />
        </div>
      </td>
      <td className='px-3 py-2 text-right tabular-nums'>
        {Math.round(node.value * 10) / 10}
      </td>
      <td className='px-3 py-2 text-right tabular-nums'>
        <Change node={node} />
      </td>
    </tr>
  )
}

/**
 * The world as the professor sees it during play: every stock by the team
 * that holds it, how far it has moved from where it started, the drift rules,
 * and the graph. Players never see this page.
 */
export default function WorldViewPage() {
  const { roomId } = useParams<{ roomId: string }>()
  const wg = useWorldGraph(roomId)
  const { state } = wg
  const [view, setView] = useState<View | null>(null)
  const [selectedNode, setSelectedNode] = useState<string | null>(null)
  const [selectedEdge, setSelectedEdge] = useState<string | null>(null)
  const groups = useMemo(() => (state ? groupsOf(state) : []), [state])
  const ownerColors = useMemo(
    () => ownerColorsFrom(state?.graph.nodes ?? []),
    [state]
  )

  if (wg.loading && !state) {
    return (
      <div className='flex justify-center py-16 text-dark-blue'>
        <Loader2 className='h-6 w-6 animate-spin' aria-label='Loading' />
      </div>
    )
  }
  if (wg.loadError?.status === 404) return <RoomNotFound />
  if (!state) {
    return (
      <div className='container mx-auto max-w-5xl px-4 py-8'>
        <div className='rounded-md bg-red-50 p-6 text-red-700'>
          {wg.loadError?.message || 'Could not load the world graph.'}
        </div>
      </div>
    )
  }

  const setupLink = roomId ? (
    <Link
      className='font-medium text-dark-blue underline'
      to={ROUTES.PROFESSOR.ROOM_CONFIGURE_WORLD_GRAPH(roomId)}
    >
      World Graph setup
    </Link>
  ) : null

  if (!state.enabled || !state.ready) {
    return (
      <div className='container mx-auto max-w-5xl px-4 py-8'>
        <Card className='bg-white shadow-md' data-testid='world-view-empty'>
          <CardContent className='p-6 text-sm text-muted-foreground'>
            {!state.enabled
              ? 'This room does not use a world graph.'
              : 'The world graph is not finished yet.'}{' '}
            You can {state.enabled ? 'finish' : 'turn it on'} in {setupLink}.
          </CardContent>
        </Card>
      </div>
    )
  }

  const { nodes, edges } = state.graph
  const labels = Object.fromEntries(nodes.map((n) => [n.id, n.label]))
  const teamNames = Object.fromEntries(state.teams.map((t) => [t.id, t.name]))
  const moved = nodes.some((n) => (n.baseline_value ?? n.value) !== n.value)
  // Open on the rounds once the game has decisions to play.
  const shown: View =
    view ??
    (state.round_options.length || state.rounds.length ? 'rounds' : 'teams')
  const node = nodes.find((n) => n.id === selectedNode)
  const edge = edges.find((e) => e.id === selectedEdge)

  return (
    <div className='min-h-screen'>
      <div
        className='container mx-auto max-w-6xl space-y-6 px-4 py-8'
        data-testid='world-view'
      >
        <Card className='bg-white shadow-md'>
          <CardHeader>
            <div className='flex flex-wrap items-start justify-between gap-4'>
              <div>
                <CardTitle className='flex items-center gap-2 text-lg text-dark-blue'>
                  <Globe className='h-5 w-5' aria-hidden />
                  The world
                </CardTitle>
                <CardDescription className='max-w-2xl'>
                  {nodes.length} stocks, {edges.length} causal links and{' '}
                  {state.tick_rules.length} drift rules.{' '}
                  {moved
                    ? 'Changes are measured from where each stock started.'
                    : 'Every stock is still at its starting value.'}{' '}
                  Players never see this page.
                </CardDescription>
              </div>
              <div className='flex gap-2'>
                <div
                  className='inline-flex rounded-md border p-0.5'
                  role='group'
                  aria-label='View'
                >
                  {(
                    [
                      ['rounds', 'Rounds', Flag],
                      ['teams', 'By team', List],
                      ['graph', 'Graph', Network],
                    ] as const
                  ).map(([key, label, Icon]) => (
                    <Button
                      key={key}
                      size='sm'
                      variant='ghost'
                      aria-pressed={shown === key}
                      className={cn(
                        'gap-1',
                        shown === key &&
                          'bg-dark-blue text-white hover:bg-dark-blue-hovered hover:text-white'
                      )}
                      onClick={() => setView(key)}
                    >
                      <Icon className='h-4 w-4' aria-hidden />
                      {label}
                    </Button>
                  ))}
                </div>
                <Button
                  size='sm'
                  variant='outline'
                  onClick={() => wg.refresh()}
                >
                  <RefreshCw className='h-4 w-4' aria-hidden />
                  Refresh
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>

        {shown === 'rounds' ? (
          <RoundPanel state={state} wg={wg} />
        ) : shown === 'teams' ? (
          groups.map((group) => (
            <Card
              key={group.key || 'shared'}
              className='bg-white shadow-md'
              data-testid='world-view-group'
            >
              <CardHeader className='pb-2'>
                <CardTitle className='flex flex-wrap items-center gap-2 text-base text-dark-blue'>
                  {group.key && (
                    <span
                      className='inline-block h-3 w-3 rounded-full'
                      style={{ backgroundColor: ownerColors.get(group.key) }}
                      aria-hidden
                    />
                  )}
                  {group.title}
                  {group.playsAs && (
                    <span className='text-sm font-normal text-muted-foreground'>
                      plays as {group.playsAs}
                    </span>
                  )}
                  {group.missingTeam && (
                    <span className='text-sm font-normal text-amber-700'>
                      No room team yet
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className='overflow-x-auto rounded-lg border'>
                  <table className='w-full text-sm'>
                    <thead className='bg-muted/50 text-left text-xs uppercase text-muted-foreground'>
                      <tr>
                        <th className='px-3 py-2'>Stock</th>
                        <th className='px-3 py-2'>Type</th>
                        <th className='px-3 py-2'>Level</th>
                        <th className='px-3 py-2 text-right'>Value</th>
                        <th className='px-3 py-2 text-right'>Change</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.nodes.map((n) => (
                        <StockRow
                          key={n.id}
                          node={n}
                          team={group.key ? group.title : ''}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <Card className='bg-white shadow-md'>
            <CardContent className='space-y-3 p-4'>
              <GraphCanvas
                nodes={nodes}
                edges={edges}
                teamNames={teamNames}
                selectedNodeId={selectedNode}
                selectedEdgeId={selectedEdge}
                onSelectNode={(id) => {
                  setSelectedEdge(null)
                  setSelectedNode((current) => (current === id ? null : id))
                }}
                onSelectEdge={(id) => {
                  setSelectedNode(null)
                  setSelectedEdge((current) => (current === id ? null : id))
                }}
              />
              {node && (
                <div className='rounded-lg border p-3 text-sm'>
                  <div className='font-medium text-dark-blue'>
                    {node.label}: {Math.round(node.value * 10) / 10}
                  </div>
                  {node.measures && (
                    <p className='text-muted-foreground'>{node.measures}</p>
                  )}
                </div>
              )}
              {edge && (
                <div className='rounded-lg border p-3 text-sm'>
                  <div className='font-medium text-dark-blue'>
                    {labels[edge.source] || edge.source}{' '}
                    {edge.sign === '+' ? 'raises' : 'lowers'}{' '}
                    {labels[edge.target] || edge.target}, strength{' '}
                    {edge.magnitude}
                    {edge.lag
                      ? `, after ${edge.lag} round${edge.lag === 1 ? '' : 's'}`
                      : ', at once'}
                  </div>
                  {edge.mechanism && (
                    <p className='text-muted-foreground'>{edge.mechanism}</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {state.tick_rules.length > 0 && (
          <Card className='bg-white shadow-md'>
            <CardHeader className='pb-2'>
              <CardTitle className='text-base text-dark-blue'>
                Moves on its own each round
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className='space-y-1 text-sm'>
                {state.tick_rules.map((rule) => (
                  <li key={rule.id}>
                    <span className='font-medium text-dark-blue'>
                      {labels[rule.target] || rule.target}
                    </span>{' '}
                    {rule.delta > 0 ? '+' : ''}
                    {rule.delta} a round
                    {rule.start_round > 1
                      ? ` from round ${rule.start_round}`
                      : ''}
                    {rule.condition && rule.condition !== 'always' && (
                      <>
                        {' '}
                        while <code className='text-xs'>{rule.condition}</code>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}
